import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import type { Review } from '@/features/memberReviews/memberReviews.types'
import { useFormatter } from '@/utils/useFormatter'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Text } from 'ui/src/components/Text/Text'
import { xStack } from 'ui/src/patterns/stack'
import { Stars } from './Stars'
import { VerifiedIcon } from './VerifiedIcon'
import { reviewContent, reviewContentRestrictedHeight, reviewFooter, reviewHeader, wrapper } from './ReviewComment.css'

const TRUNCATED_CONTENT_MAX_LENGTH = 125

type Props = Review & {
  className?: string
  restrictHeight?: boolean
}

export function ReviewComment({ score, date, content, attributedTo, className, restrictHeight = false }: Props) {
  const { t } = useTranslation('reviews')
  const tag = useTag(attributedTo)
  const formatter = useFormatter()

  const displayedContent = restrictHeight ? truncate(content, TRUNCATED_CONTENT_MAX_LENGTH) : content

  return (
    <div className={clsx(wrapper, className)}>
      <div className={reviewHeader}>
        <Stars score={score} size="1rem" />
        <Badge size="small" variant="outline">
          {tag}
        </Badge>
      </div>

      <Text className={clsx(reviewContent, restrictHeight && reviewContentRestrictedHeight)} size="md">
        {displayedContent}
      </Text>

      <div className={reviewFooter}>
        <div className={xStack({ gap: 'xxs' })}>
          <VerifiedIcon />

          <Text as="span" size="xs">
            {t('VERIFIED_CUSTOMER_LABEL')}
          </Text>
        </div>

        <Text as="span" size="xs">
          {formatter.dateFull(new Date(date), { abbreviateMonth: true })}
        </Text>
      </div>
    </div>
  )
}

// Truncate the text at the last space before the max length, so that we don't cut off a word.
function truncate(text: string, maxLength: number) {
  if (text.length <= maxLength) {
    return text
  }
  const sliced = text.slice(0, maxLength)
  const lastSpaceIndex = sliced.lastIndexOf(' ')
  const truncated = lastSpaceIndex > 0 ? sliced.slice(0, lastSpaceIndex) : sliced
  return `${truncated.trimEnd()}…`
}

function useTag(attributedTo: string) {
  const { t } = useTranslation('reviews')

  switch (attributedTo) {
    case 'SE_PET_DOG':
      return t('SE_PET_DOG_REVIEW_COMMENT_TAG')
    case 'SE_PET_CAT':
      return t('SE_PET_CAT_REVIEW_COMMENT_TAG')
    case 'SE_HOUSE':
      return t('SE_HOUSE_REVIEW_COMMENT_TAG')
    case 'SE_APARTMENT_STUDENT':
      return t('SE_APARTMENT_STUDENT_REVIEW_COMMENT_TAG')
    case 'SE_ACCIDENT':
      return t('SE_ACCIDENT_REVIEW_COMMENT_TAG')
    case 'SE_CAR':
      return t('SE_CAR_REVIEW_COMMENT_TAG')
    case 'SE_APARTMENT_BRF':
      return t('SE_APARTMENT_BRF_REVIEW_COMMENT_TAG')
    case 'SE_APARTMENT_RENT':
      return t('SE_APARTMENT_RENT_REVIEW_COMMENT_TAG')
    default:
      return attributedTo
  }
}
